import React, { useRef, useState } from 'react';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import AuthLayout from './AuthLayout';
import AuthField from './AuthField';
import clientLogger from '../../utils/clientLogger';

const ROLE_TABS = [
    { role: 'driver', label: 'Driver', to: '/driverSignin' },
    { role: 'admin', label: 'Staff', to: '/adminsignin' },
    { role: 'partner', label: 'Customer', to: '/partnerSignin' },
];

const rememberKey = role => `ttms.signin.username.${role}`;

const readRemembered = role => {
    try { return localStorage.getItem(rememberKey(role)) || ''; } catch { return ''; }
};

const writeRemembered = (role, username) => {
    try {
        if (username) localStorage.setItem(rememberKey(role), username);
        else localStorage.removeItem(rememberKey(role));
    } catch { /* storage unavailable — nothing to remember */ }
};

/**
 * Shared sign-in screen for drivers, staff and customers.
 *
 * signin(credentials) -> Promise<data | { error }>, authenticate(data, next)
 * stores the session, redirectTo(data) picks where to land afterwards.
 */
export default function SignInPage({
    role,
    roleLabel,
    bannerRole,
    brand,
    title,
    subtitle,
    signin,
    authenticate,
    redirectTo,
    forgotPath,
    help,
}) {
    const location = useLocation();
    const remembered = readRemembered(role);
    const [username, setUsername] = useState(remembered);
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(!!remembered);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [target, setTarget] = useState(null);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);

    if (target) return <Redirect to={target} />;

    const onSubmit = event => {
        event.preventDefault();
        if (loading) return;

        const user = username.trim();
        const missing = {};
        if (!user) missing.username = 'Enter your username';
        if (!password) missing.password = 'Enter your password';
        setFieldErrors(missing);
        setError('');
        if (missing.username) return usernameRef.current?.focus();
        if (missing.password) return passwordRef.current?.focus();

        setLoading(true);
        signin({ username: user, password })
            .then(data => {
                if (!data) {
                    setError("We couldn't reach the server. Check your connection and try again.");
                    setLoading(false);
                    return;
                }
                if (data.error) {
                    setError(data.error);
                    setLoading(false);
                    clientLogger.logError(`${roleLabel}Signin`, `Error logging in as ${roleLabel.toLowerCase()}`, user, data.error);
                    return;
                }
                clientLogger.logInfo(`${roleLabel}Signin`, `${roleLabel} logged in successfully`, user);
                writeRemembered(role, remember ? user : '');
                authenticate(data, () => {
                    setTarget(location.state?.from || redirectTo(data));
                });
            })
            .catch(err => {
                setError(`Something went wrong signing you in. Please try again.`);
                setLoading(false);
                clientLogger.logCritical(`${roleLabel}Signin`, `Error logging in as ${roleLabel.toLowerCase()}`, user, err);
            });
    };

    return (
        <AuthLayout bannerRole={bannerRole} brand={brand}>
            <nav className="ta-roles" aria-label="Account type">
                {ROLE_TABS.map(tab => (
                    <Link
                        key={tab.role}
                        to={tab.to}
                        className="ta-role"
                        aria-current={tab.role === role ? 'page' : undefined}
                    >
                        {tab.label}
                    </Link>
                ))}
            </nav>

            <h2 className="ta-title">{title}</h2>
            <p className="ta-sub">{subtitle}</p>

            <form onSubmit={onSubmit} noValidate>
                <div className="ta-fields">
                    <AuthField
                        id={`${role}-username`}
                        label="Username"
                        inputRef={usernameRef}
                        value={username}
                        onChange={e => { setUsername(e.target.value); setFieldErrors(f => ({ ...f, username: '' })); }}
                        error={fieldErrors.username}
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        autoFocus={!remembered}
                    />
                    <AuthField
                        id={`${role}-password`}
                        label="Password"
                        type="password"
                        inputRef={passwordRef}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setFieldErrors(f => ({ ...f, password: '' })); }}
                        error={fieldErrors.password}
                        autoComplete="current-password"
                        autoFocus={!!remembered}
                    />
                </div>

                <div className="ta-row">
                    <label className="ta-check">
                        <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />
                        Remember username
                    </label>
                    {forgotPath && <Link to={forgotPath} className="ta-link">Forgot password?</Link>}
                </div>

                {error && (
                    <div className="ta-alert" role="alert">
                        <FiAlertCircle size={18} aria-hidden="true" />
                        <span>{error}</span>
                    </div>
                )}

                <button type="submit" className="ta-btn" disabled={loading}>
                    {loading
                        ? <><span className="ta-spinner" aria-hidden="true" /> Signing in…</>
                        : <>Sign in <FiArrowRight size={18} aria-hidden="true" /></>}
                </button>
            </form>

            {help && <p className="ta-help">{help}</p>}
        </AuthLayout>
    );
}
